import { AddCircleOutlineRounded, CloseRounded, ExpandMoreRounded } from '@mui/icons-material'
import DragIcon from '@mui/icons-material/DragIndicator'
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { DragItem, DragList, SwalContext } from 'genesis-suite/components'
import { EditBox, Trash } from 'genesis-suite/icons'
import { MouseEventHandler, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { widgetCreators } from '../../actions/creators'
import MenuButton from '../MenuButton'
import ConfigureAction from './ConfigureAction'
import WidgetActionButton from './WidgetActionButton'
import WidgetActionMenuItem from './WidgetActionMenuItem'
import Measure from 'react-measure'
import { useDebouncedCallback } from 'use-debounce'
import { sleep } from 'genesis-suite/utils'
import { computeHiddenCount } from '~/lib/utils'

const useStyles = makeStyles(({ spacing, palette }) => ({
    root: { marginTop: spacing(0.5) },
    actionDialog: { '& .MuiDialog-paper': { width: '100%', maxWidth: 400, paddingBottom: spacing(1.8) } },
    widgetModifyDialog: { '& .MuiDialog-paper': { width: '100%', maxWidth: 400, paddingBottom: spacing(1.8) } },
    actionHeader: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        padding: spacing(0.5, 1),
        justifyContent: 'space-between',
    },
    hideIcon: {
        display: 'none',
    },
    ConfigureButtonIconStyle: { color: palette.text.primary },
}))

const generateKey = () => Math.random().toString(36).substr(2, 9)

export default function WidgetActionController({
    config,
    isEditing,
    visualRef,
    filters,
    inlineFilters,
    networkContext,
}) {
    const classes = useStyles({})
    const { Actions, version, Type } = config

    const restProps = { config, filters, inlineFilters, networkContext }

    const [open, setOpen] = useState(false)
    const [editAction, setEditAction] = useState(null)
    // Actions don't have ids nor a way to guarantee uniqueness
    const [actions, setActions] = useState(Actions ? Actions.map(a => ({ ...a, id: generateKey() })) : [])
    const [recompute, setRecompute] = useState(false)
    const [hiddenCount, setHiddenCount] = useState(0)
    const [layingOut, setLayingOut] = useState(true)

    const { confirm } = useContext(SwalContext)
    const dispatch = useDispatch()
    const parentRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (editAction && !open) setOpen(true)
    }, [editAction])

    useEffect(() => {
        if (Type === 'Chart') visualRef.current?.chart?.reflow()
    }, [isEditing])

    const [newDialogOpen, setNewDialogOpen] = useState(false)
    const handleResize = useDebouncedCallback(resetLayout, 500)

    useEffect(() => {
        setHiddenCount(computeHiddenCount(parentRef, true, 32))
        setLayingOut(false)
    }, [recompute])

    if (version === '2' || (!actions.length && !isEditing)) return null

    const handleSave = newActions => {
        const Actions = newActions.map(a => {
            const { id, ...rest } = a
            return rest
        })
        //@ts-ignore
        return dispatch(widgetCreators.saveWidget({ ...config, Actions })).then(() => {
            setActions(newActions)
            reset()
        })
    }

    async function resetLayout() {
        setLayingOut(true)
        await sleep()
        setRecompute(s => !s)
    }

    const reset = () => {
        setOpen(false)
        setEditAction(null)
    }
    const handleClick: MouseEventHandler<HTMLDivElement> = event => {
        event.stopPropagation()
    }

    const handleDragEnd = newItems => {
        setActions(newItems)
    }

    const handleAddNew = () => {
        setEditAction({ id: generateKey() })
    }

    const openNewDialog = () => {
        setNewDialogOpen(true)
    }

    const closeNewDialog = () => {
        setNewDialogOpen(false)
    }

    const handleClose = async () => {
        const response = await confirm('You will lose all changes on your action, continue?')
        if (response.dismiss) return
        reset()
    }

    const confirmDelete = async id => {
        const response = await confirm('Delete this action?')
        if (response.dismiss) return
        const newActions = actions.filter(a => a.id !== id)

        return handleSave(newActions)
    }

    const handleSubmit = actionDraft => {
        const editing = actions.find(a => a.id === actionDraft.id)
        const newActions = editing
            ? actions.map(a => {
                  if (a.id === actionDraft.id) return actionDraft
                  return a
              })
            : [...actions, actionDraft]
        return handleSave(newActions)
    }

    const visibleItems = layingOut ? actions : actions.slice(0, actions.length - hiddenCount)
    const hiddenItems = actions.slice(-hiddenCount)
    const itemHeight = 50
    const itemWidth = 350
    return (
        <Measure client innerRef={parentRef} onResize={handleResize}>
            {({ measureRef }) => (
                <Box ref={measureRef} className={classes.root} style={{ display: 'flex', alignItems: 'center' }}>
                    {visibleItems.map(action => (
                        <WidgetActionButton
                            key={action.id}
                            action={action}
                            onClick={isEditing ? setEditAction : null}
                            hidden={layingOut}
                            {...restProps}
                        />
                    ))}

                    <Box sx={{ ml: 1 }}>
                        {hiddenCount > 0 && (
                            <MenuButton title={<ExpandMoreRounded />}>
                                {hiddenItems.map(a => (
                                    <WidgetActionMenuItem key={a.id} action={a} {...restProps} />
                                ))}
                            </MenuButton>
                        )}
                        {isEditing && (
                            <Tooltip title="Configure Widget Actions">
                                {/* z-index set higher than widget edit cover */}
                                <IconButton
                                    sx={{ zIndex: 3 }}
                                    onClick={() => {
                                        openNewDialog()
                                    }}
                                >
                                    <EditBox className={classes.ConfigureButtonIconStyle} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    <Dialog
                        className={classNames('noDrag', classes.widgetModifyDialog)}
                        open={newDialogOpen}
                        onClose={closeNewDialog}
                    >
                        <DialogTitle className={classes.actionHeader}>
                            <Typography variant="h6">Configure Actions</Typography>
                            <IconButton onClick={closeNewDialog}>
                                <CloseRounded />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent onClick={handleClick}>
                            {actions.length ? (
                                <>
                                    <ListItemText
                                        sx={{ pl: 1 }}
                                        primary="Actions in this box will appear as buttons on the widget."
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bgcolor: 'grayscale.lighter',
                                            border: 1,
                                            borderRadius: 1,
                                            height: itemHeight * Math.min(actions.length, 3),
                                            width: itemWidth,
                                        }}
                                    />
                                </>
                            ) : (
                                <ListItemText primary='No actions found. Click "New Action" to add.' />
                            )}
                            <DragList onDragEnd={handleDragEnd} items={actions}>
                                {actions.map((action, index) => (
                                    <WidgetActionDragItem
                                        key={action.id}
                                        onClick={() => setEditAction(action)}
                                        width={itemWidth}
                                        height={itemHeight}
                                        action={action}
                                        index={index}
                                        onDelete={confirmDelete}
                                    />
                                ))}
                            </DragList>
                        </DialogContent>
                        <div style={{ marginLeft: '16px' }}>
                            <Tooltip title="Add New">
                                <Button
                                    onClick={handleAddNew}
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<AddCircleOutlineRounded />}
                                >
                                    New Action
                                </Button>
                            </Tooltip>
                        </div>
                    </Dialog>

                    <Dialog className={classes.actionDialog} open={open} onClose={handleClose}>
                        <div className={classes.actionHeader}>
                            <Typography variant="h6">Configure Action</Typography>
                            <IconButton onClick={handleClose}>
                                <CloseRounded />
                            </IconButton>
                        </div>
                        <DialogContent>
                            <ConfigureAction
                                action={editAction}
                                onSubmit={handleSubmit}
                                onDelete={editAction?.ActionName ? confirmDelete : null}
                            />
                        </DialogContent>
                    </Dialog>
                </Box>
            )}
        </Measure>
    )
}

const WidgetActionDragItem = ({ action, index, onDelete, height, width, onClick }) => {
    return (
        <DragItem key={action.id} itemId={action.id} index={index}>
            <Tooltip title="Click to Edit" placement="top">
                <ListItemButton
                    sx={{
                        width,
                        height,
                        padding: '8px',
                        display: 'flex',
                        borderRadius: '4px',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid transparent',
                        transition: 'all 350ms ease',
                        '& .hide': {
                            opacity: 0,
                            transition: 'all 350ms ease',
                        },
                        '&:hover': {
                            borderColor: 'black',
                            '& .hide': { opacity: 1 },
                        },
                    }}
                    onClick={onClick}
                >
                    <Typography variant="h6">{action.ShortName}</Typography>
                    <DragIcon className="hide" sx={{ cursor: 'grab' }} />
                    <IconButton
                        className="hide"
                        size="small"
                        onClick={e => {
                            e.stopPropagation()
                            onDelete(action.id)
                        }}
                        sx={{ color: 'text.primary' }}
                    >
                        <Trash />
                    </IconButton>
                </ListItemButton>
            </Tooltip>
        </DragItem>
    )
}
